<template>
    <v-row>
        <v-col cols="12">
            <base-card>
                <v-card-title>
                    {{ $t('admin.backups.title') }}
                </v-card-title>
                <v-card-subtitle>
                    {{ $t('admin.backups.subtitle') }}
                </v-card-subtitle>

                <v-row class="mx-4 mb-0">
                    <v-col
                        cols="12"
                        sm="6"
                        md="3"
                        class="py-1"
                    >
                        <div class="text-caption grey--text">
                            {{ $t('admin.backups.databaseName') }}
                        </div>
                        <div class="text-body-2 font-weight-medium">
                            {{ status.databaseName || '-' }}
                            <span
                                v-if="status.dbVersion"
                                class="grey--text"
                            >({{ status.dbVersion }})</span>
                        </div>
                    </v-col>

                    <v-col
                        cols="12"
                        sm="6"
                        md="3"
                        class="py-1"
                    >
                        <div class="text-caption grey--text">
                            {{ $t('admin.backups.currentYear') }}
                        </div>
                        <div class="text-body-2 font-weight-medium">
                            {{ status.currentEditionYear || '-' }}
                        </div>
                    </v-col>

                    <v-col
                        cols="12"
                        sm="6"
                        md="3"
                        class="py-1"
                    >
                        <div class="text-caption grey--text">
                            {{ $t('admin.backups.lastBackupAt') }}
                        </div>
                        <div class="text-body-2 font-weight-medium">
                            {{ status.lastBackupAt ? formatDateTime(status.lastBackupAt) : $t('admin.backups.never') }}
                        </div>
                    </v-col>

                    <v-col
                        cols="12"
                        sm="6"
                        md="3"
                        class="py-1"
                    >
                        <div class="text-caption grey--text">
                            {{ $t('admin.backups.freeSpace') }}
                        </div>
                        <div class="text-body-2 font-weight-medium">
                            {{ status.diskFreeBytes ? formatBytes(status.diskFreeBytes) : '-' }}
                        </div>
                    </v-col>
                </v-row>

                <v-alert
                    v-if="destructiveBlocked"
                    class="mx-4 mt-2"
                    type="warning"
                    dense
                    text
                >
                    {{ $t('admin.backups.destructiveDisabled') }}
                    <span v-if="missingCapabilities.length">
                        {{ $t('admin.backups.missingCapabilities', { caps: missingCapabilities.join(', ') }) }}
                    </span>
                </v-alert>

                <v-alert
                    v-if="!status.diskWritable"
                    class="mx-4 mt-2"
                    type="error"
                    dense
                    text
                >
                    {{ $t('admin.backups.diskNotWritable') }}
                </v-alert>

                <!--
                    Un mismo aviso para los dos restos que deja una operacion
                    que muere a mitad: el cerrojo puesto (bloquea toda la API)
                    y la fila que se quedo "en curso" (el panel la consulta sin
                    parar y no deja borrarla). Los dos se limpian con el mismo
                    boton, que es la unica alternativa a `artisan backup:unlock`
                    para quien administra sin acceso a la consola del servidor.
                -->
                <v-alert
                    v-if="stuck"
                    class="mx-4 mt-2"
                    color="orange"
                    dense
                    text
                    icon="mdi-lock-clock"
                >
                    <div class="d-flex align-center flex-wrap">
                        <span>
                            <template v-if="status.locked">
                                {{ $t('admin.backups.lockedSince', { date: formatDateTime(status.lockedSince) }) }}
                                <span v-if="status.lockedOperation || status.lockedBy">
                                    {{ $t('admin.backups.lockedDetail', {
                                        operation: operationLabel(status.lockedOperation),
                                        user: status.lockedBy || '-'
                                    }) }}
                                </span>
                            </template>
                            <template v-else>
                                {{ $t('admin.backups.staleRunning', { count: status.staleRunning }) }}
                            </template>
                        </span>
                        <v-spacer />
                        <v-btn
                            small
                            outlined
                            color="orange darken-3"
                            :loading="unlocking"
                            @click="unlock"
                        >
                            <v-icon small>
                                mdi-lock-open-variant
                            </v-icon>
                            {{ $t('admin.backups.unlock') }}
                        </v-btn>
                    </div>
                </v-alert>

                <v-card-title>
                    <div class="d-flex justify-space-between flex-wrap">
                        <v-btn
                            class="ma-2"
                            dark
                            color="primary"
                            :disabled="busy"
                            @click="openCreateDialog"
                        >
                            <v-icon>mdi-database-plus</v-icon>
                            {{ $t('admin.backups.create') }}
                        </v-btn>
                        <!--
                            Aqui NO hay boton de cierre de anio: la operacion
                            vive en Configuraciones Generales, junto a las
                            ventanas de fechas que reescribe. Duplicarla daria
                            dos caminos para vaciar la plataforma.
                        -->
                        <v-btn
                            outlined
                            class="ma-2"
                            :loading="uploading"
                            :disabled="busy"
                            @click="pickFile"
                        >
                            <v-icon>mdi-upload</v-icon>
                            {{ $t('admin.backups.upload') }}
                        </v-btn>
                    </div>
                </v-card-title>

                <v-card-title>
                    {{ $t('admin.backups.listTitle') }}
                    <v-spacer />
                    <v-spacer />
                    <v-spacer />
                    <v-spacer />

                    <v-text-field
                        v-model="search"
                        :append-icon="!search ? 'mdi-magnify' : undefined"
                        clearable
                        :label="$t('common.search')"
                        single-line
                        hide-details
                    />
                </v-card-title>

                <v-data-table
                    :options.sync="options"
                    :headers="headers"
                    :footer-props="{ showFirstLastPage: true,
                                     'items-per-page-options': [15, 30, 50, -1] }"
                    :items="items"
                    class="elevation-1"
                    :items-per-page="15"
                    :sort-by="sortby"
                    :sort-desc="[true]"
                    :multi-sort="false"
                    :search="search"
                    :no-data-text="$t('admin.backups.noData')"
                >
                    <template v-slot:item.createdAt="{ item }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <span
                                    v-bind="attrs"
                                    v-on="on"
                                >{{ formatDateTime(item.createdAt) }}</span>
                            </template>

                            <span>{{ item.filename }}</span>
                        </v-tooltip>
                    </template>

                    <!--
                        La etiqueta sola no explica de donde sale una copia que
                        el administrador no ha pedido: las automaticas aparecen
                        despues de restaurar y se leen como filas duplicadas. El
                        tooltip dice para que sirve cada tipo.
                    -->
                    <template v-slot:item.type="{ value }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    small
                                    :color="typeColor(value)"
                                    dark
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ typeLabel(value) }}
                                </v-chip>
                            </template>

                            <span>{{ typeHint(value) }}</span>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <v-progress-circular
                            v-if="item.status === 'running'"
                            indeterminate
                            size="20"
                            width="2"
                            color="info"
                        />
                        <v-tooltip
                            v-else-if="item.status === 'failed'"
                            top
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    small
                                    color="error"
                                    dark
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ statusLabel(item.status) }}
                                </v-chip>
                            </template>

                            <span>{{ item.errorMessage || statusLabel(item.status) }}</span>
                        </v-tooltip>
                        <v-chip
                            v-else
                            small
                            :color="statusColor(item.status)"
                            dark
                        >
                            {{ statusLabel(item.status) }}
                        </v-chip>
                    </template>

                    <template v-slot:item.sizeBytes="{ value }">
                        {{ formatBytes(value) }}
                    </template>

                    <template v-slot:item.rowsCount="{ value }">
                        {{ numberHelper.formatInteger(value) || 0 }}
                    </template>

                    <template v-slot:item.notes="{ value }">
                        <span class="text-caption">{{ value }}</span>
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    small
                                    class="mr-2"
                                    :disabled="item.status !== 'completed' || busy"
                                    v-bind="attrs"
                                    @click="download(item)"
                                    v-on="on"
                                >
                                    mdi-download
                                </v-icon>
                            </template>

                            <span>{{ $t('admin.backups.actions.download') }}</span>
                        </v-tooltip>

                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    small
                                    class="mr-2"
                                    :disabled="item.status !== 'completed' || busy || destructiveBlocked"
                                    v-bind="attrs"
                                    @click="openRestoreDialog(item)"
                                    v-on="on"
                                >
                                    mdi-database-import
                                </v-icon>
                            </template>

                            <span>{{ $t('admin.backups.actions.restore') }}</span>
                        </v-tooltip>

                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    small
                                    :disabled="item.status === 'running' || busy"
                                    v-bind="attrs"
                                    @click="deleteItem(item)"
                                    v-on="on"
                                >
                                    mdi-delete
                                </v-icon>
                            </template>

                            <span>{{ $t('admin.backups.actions.delete') }}</span>
                        </v-tooltip>
                    </template>
                </v-data-table>
            </base-card>

            <!-- Crear copia -->
            <v-dialog
                v-model="createDialog.show"
                max-width="520"
                persistent
            >
                <v-card>
                    <v-card-title class="text-h6">
                        {{ $t('admin.backups.createDialog.title') }}
                    </v-card-title>
                    <v-card-text>
                        <p class="text-body-2">
                            {{ $t('admin.backups.createDialog.text') }}
                        </p>
                        <v-textarea
                            v-model="createDialog.notes"
                            :label="$t('admin.backups.createDialog.notes')"
                            rows="2"
                            counter="255"
                            maxlength="255"
                        />
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            text
                            :disabled="createDialog.processing"
                            @click="closeCreateDialog"
                        >
                            {{ $t('common.cancel') }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            :loading="createDialog.processing"
                            @click="createBackup"
                        >
                            {{ $t('admin.backups.createDialog.confirm') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Restaurar copia -->
            <v-dialog
                v-model="restoreDialog.show"
                max-width="620"
                persistent
            >
                <v-card>
                    <v-card-title class="text-h6 error--text">
                        {{ $t('admin.backups.restoreDialog.title') }}
                    </v-card-title>
                    <v-card-text>
                        <v-alert
                            type="error"
                            dense
                            text
                        >
                            {{ $t('admin.backups.restoreDialog.warning') }}
                        </v-alert>

                        <p class="text-body-2 mb-1">
                            {{ $t('admin.backups.restoreDialog.autoBackup') }}
                        </p>
                        <p class="text-body-2 font-weight-medium">
                            {{ $t('admin.backups.restoreDialog.file', { filename: restoreDialog.item ? restoreDialog.item.filename : '' }) }}
                        </p>

                        <v-checkbox
                            v-model="restoreDialog.understood"
                            :label="$t('admin.backups.restoreDialog.understand')"
                            hide-details
                            class="mt-0"
                        />

                        <v-text-field
                            v-model="restoreDialog.confirmText"
                            class="mt-4"
                            :label="$t('admin.backups.restoreDialog.typeName', { name: status.databaseName })"
                            autocomplete="off"
                        />
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            text
                            :disabled="restoreDialog.processing"
                            @click="closeRestoreDialog"
                        >
                            {{ $t('common.cancel') }}
                        </v-btn>
                        <v-btn
                            color="error"
                            :disabled="!restoreReady"
                            :loading="restoreDialog.processing"
                            @click="restoreBackup"
                        >
                            {{ $t('admin.backups.restoreDialog.confirm') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <confirm-dialog
                ref="confirm"
            />

            <input
                ref="file"
                type="file"
                hidden
                accept=".zip,application/zip"
                @change="selectedFileToUpload"
            >
        </v-col>
    </v-row>
</template>

<script>
import axios from '@/plugins/axios'
import { errorMessageKey, validationMessages } from '@/helpers/apiError'

// Crear, restaurar y resetear vuelcan la base de datos entera: los 20 s por
// defecto de plugins/axios.js se agotan mucho antes de que el servidor termine.
const LONG_TIMEOUT = 600000;

// Cada cuanto se vuelve a preguntar por la lista mientras hay una copia en curso.
const POLL_INTERVAL = 5000;

export default {
    name: 'BackupsList',
    data: function () {
        return {
            urlMain: 'backups',
            headers: [
                {
                    text: this.$t('admin.backups.table.createdAt'),
                    align: 'start',
                    value: 'createdAt'
                },
                {
                    text: this.$t('admin.backups.table.year'),
                    value: 'editionYear'
                },
                {
                    text: this.$t('admin.backups.table.type'),
                    value: 'type',
                    // La etiqueta mas larga ('Copia anterior a la restauracion')
                    // parte el chip en dos lineas y desalinea la fila si la
                    // columna se reparte sola. El ancho se lo quita 'Notas',
                    // que ya va a varias lineas por los nombres de fichero.
                    width: '220px'
                },
                {
                    text: this.$t('admin.backups.table.status'),
                    value: 'status'
                },
                {
                    text: this.$t('admin.backups.table.size'),
                    value: 'sizeBytes'
                },
                {
                    text: this.$t('admin.backups.table.rows'),
                    value: 'rowsCount'
                },
                {
                    text: this.$t('admin.backups.table.tables'),
                    value: 'tablesCount'
                },
                {
                    text: this.$t('admin.backups.table.createdBy'),
                    value: 'createdByUsername'
                },
                {
                    text: this.$t('admin.backups.table.notes'),
                    value: 'notes',
                    sortable: false
                },
                {
                    text: this.$t('admin.backups.table.actions'),
                    value: 'actions',
                    sortable: false
                }
            ],
            items: [],
            search: '',
            sortby: ['createdAt'],
            options: {
                itemsPerPage: 15
            },
            maxUploadBytes: 0,
            uploading: false,
            unlocking: false,
            status: {
                databaseName: '',
                dbVersion: '',
                destructiveAllowed: true,
                locked: false,
                lockedSince: null,
                lockedBy: null,
                lockedOperation: null,
                staleRunning: 0,
                capabilities: {},
                currentEditionYear: null,
                availableYears: [],
                lastBackupAt: null,
                diskWritable: true,
                diskFreeBytes: 0
            },
            createDialog: {
                show: false,
                notes: '',
                processing: false
            },
            restoreDialog: {
                show: false,
                item: null,
                understood: false,
                confirmText: '',
                processing: false
            },
            pollTimer: null
        }
    },
    computed: {
        // Privilegios de BBDD que faltan para poder restaurar o resetear.
        missingCapabilities() {
            const capabilities = this.status.capabilities ?? {};

            return ['create', 'drop', 'alter', 'references']
                .filter(name => capabilities[name] === false)
                .map(name => name.toUpperCase());
        },
        destructiveBlocked() {
            return this.status.destructiveAllowed === false || this.missingCapabilities.length > 0;
        },
        // El servidor decide cuando una copia "en curso" lleva demasiado
        // tiempo: restar su fecha con el reloj del navegador daria un aviso
        // distinto en cada equipo.
        stuck() {
            return this.status.locked === true || (this.status.staleRunning ?? 0) > 0;
        },
        // Mientras hay una operacion larga en marcha no se deja lanzar otra: el
        // backend responde 423 y el usuario solo veria un error sin contexto.
        busy() {
            return this.status.locked === true ||
                this.uploading ||
                this.createDialog.processing ||
                this.restoreDialog.processing;
        },
        restoreReady() {
            return this.restoreDialog.understood &&
                !!this.status.databaseName &&
                this.restoreDialog.confirmText === this.status.databaseName;
        }
    },
    created() {
        this.refresh();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        async refresh() {
            this.$store.dispatch('loading/start');

            await Promise.all([this.fetchStatus(), this.fetchBackups()]);

            this.$store.dispatch('loading/end');
        },
        async fetchStatus() {
            try {
                const response = await axios.get(this.urlMain + '/status');

                this.status = Object.assign({}, this.status, response.data ?? {});
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.$t('admin.backups.errors.load'));
            }
        },
        async fetchBackups() {
            try {
                const response = await axios.get(this.urlMain);

                this.items = response?.data?.backups ?? [];
                this.maxUploadBytes = response?.data?.maxUploadBytes ?? 0;

                this.syncPolling();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.$t('admin.backups.errors.load'));
            }
        },
        // El volcado corre en el servidor: la fila queda en 'running' y hay que
        // ir preguntando hasta que cambia de estado, sin recargar la pagina.
        syncPolling() {
            const running = this.items.some(item => item.status === 'running');

            if (running && !this.pollTimer) {
                this.pollTimer = setInterval(() => {
                    this.fetchBackups();
                    this.fetchStatus();
                }, POLL_INTERVAL);
            } else if (!running) {
                this.stopPolling();
            }
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        openCreateDialog() {
            this.createDialog.notes = '';
            this.createDialog.show = true;
        },
        closeCreateDialog() {
            if (!this.createDialog.processing) {
                this.createDialog.show = false;
            }
        },
        async createBackup() {
            if (this.createDialog.processing) {
                return;
            }

            this.createDialog.processing = true;

            try {
                await axios.post(this.urlMain, {
                    year: this.status.currentEditionYear ?? null,
                    notes: this.createDialog.notes || null
                }, { timeout: LONG_TIMEOUT });

                this.createDialog.show = false;
                this.$store.dispatch('snackbar/success', this.$t('admin.backups.createDialog.success'));

                await this.refresh();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.resolveError(error, 'admin.backups.createDialog.error'));
            } finally {
                this.createDialog.processing = false;
            }
        },
        pickFile() {
            this.$refs.file.click();
        },
        async selectedFileToUpload(event) {
            if (!(event.target.files && event.target.files.length)) {
                return;
            }

            const file = event.target.files[0];

            // Se comprueba aqui tambien porque un ZIP grande tarda en subir antes
            // de que el servidor pueda contestar 413/422.
            if (this.maxUploadBytes && file.size > this.maxUploadBytes) {
                const size = this.formatBytes(this.maxUploadBytes);

                this.$store.dispatch('snackbar/error', this.$t('admin.backups.uploadTooLarge', { size }));
                event.target.value = null;
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            this.uploading = true;

            try {
                await axios.post(this.urlMain + '/upload', formData, {
                    timeout: LONG_TIMEOUT,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                this.$store.dispatch('snackbar/success', this.$t('admin.backups.uploadSuccess'));

                await this.refresh();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.resolveError(error, 'admin.backups.uploadError'));
            } finally {
                this.uploading = false;
                event.target.value = null;
            }
        },
        // El JWT viaja en la cabecera, asi que no vale un <a href>: hay que pedir
        // el binario con axios y montar el fichero en memoria.
        async download(item) {
            this.$store.dispatch('loading/start');

            try {
                const response = await axios.get(this.urlMain + '/' + item.id + '/download', {
                    responseType: 'arraybuffer',
                    timeout: LONG_TIMEOUT
                });

                const blob = new Blob([response.data], { type: 'application/zip' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = item.filename;
                link._target = 'blank';
                link.click();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.$t('admin.backups.errors.download'));
            }

            this.$store.dispatch('loading/end');
        },
        openRestoreDialog(item) {
            this.restoreDialog.item = item;
            this.restoreDialog.understood = false;
            this.restoreDialog.confirmText = '';
            this.restoreDialog.show = true;
        },
        closeRestoreDialog() {
            if (!this.restoreDialog.processing) {
                this.restoreDialog.show = false;
            }
        },
        async restoreBackup() {
            if (!this.restoreReady || this.restoreDialog.processing) {
                return;
            }

            this.restoreDialog.processing = true;

            try {
                const response = await axios.post(this.urlMain + '/' + this.restoreDialog.item.id + '/restore', {
                    confirm: this.restoreDialog.confirmText,
                    runMigrations: true
                }, { timeout: LONG_TIMEOUT });

                const restored = response?.data?.restored ?? {};

                this.restoreDialog.show = false;
                this.$store.dispatch('snackbar/success', this.$t('admin.backups.restoreDialog.success', {
                    tables: restored.tables ?? 0,
                    rows: restored.rows ?? 0
                }));
                this.$store.commit('cacheRecognitios/clearUsers');

                await this.refresh();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.resolveError(error, 'admin.backups.restoreDialog.error'));
            } finally {
                this.restoreDialog.processing = false;
            }
        },
        /**
         * Liberar el bloqueo no toca ni un dato, pero si la operacion original
         * siguiera viva permitiria lanzar otra encima: por eso pasa por la
         * confirmacion en lugar de ejecutarse al primer clic.
         */
        async unlock() {
            if (this.unlocking) {
                return;
            }

            const confirmed = await this.$refs.confirm.open(
                this.$t('common.confirmTitle'),
                this.$t('admin.backups.unlockConfirm')
            );

            if (!confirmed) {
                return;
            }

            this.unlocking = true;

            try {
                const response = await axios.post(this.urlMain + '/unlock');
                const data = response?.data ?? {};
                const closed = data.closedBackups ?? 0;

                // Sin nada que liberar el mensaje no puede ser el de exito: el
                // administrador se quedaria pensando que ha arreglado algo.
                this.$store.dispatch(
                    'snackbar/success',
                    data.released || closed
                        ? this.$t('admin.backups.unlockSuccess', { count: closed })
                        : this.$t('admin.backups.unlockNothing')
                );

                await this.refresh();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.resolveError(error, 'admin.backups.unlockError'));
            } finally {
                this.unlocking = false;
            }
        },
        async deleteItem(item) {
            if (
                await this.$refs.confirm.open(
                    this.$t('common.confirmTitle'),
                    this.$t('admin.backups.deleteConfirm', { filename: item.filename })
                )
            ) {
                this.deleteItemConfirmed(item);
            }
        },
        async deleteItemConfirmed(item) {
            this.$store.dispatch('loading/start');

            try {
                await axios.delete(this.urlMain + '/' + item.id);

                this.$store.dispatch('snackbar/success', this.$t('admin.backups.deleteSuccess'));

                await this.fetchBackups();
                await this.fetchStatus();
            } catch (error) {
                this.$store.dispatch('snackbar/error', this.resolveError(error, 'admin.backups.deleteError'));
            }

            this.$store.dispatch('loading/end');
        },
        /**
         * El backend distingue tres fallos que el usuario resuelve de forma muy
         * distinta: esperar (423), leer el motivo concreto (409) o pedir que se
         * habiliten las operaciones destructivas (403). Un unico "ocurrieron
         * errores" dejaria al administrador sin saber que hacer.
         */
        resolveError(error, fallbackKey) {
            const response = error?.response;
            const status = response?.status;

            if (status === 423) {
                return this.$t('admin.backups.errors.locked');
            }

            if (status === 403) {
                return this.$t('admin.backups.errors.forbidden');
            }

            if (status === 409) {
                return response?.data?.message ?? this.$t('admin.backups.errors.conflict');
            }

            if (status === 422) {
                const messages = validationMessages(error);

                if (messages.length) {
                    return messages.join(' ');
                }

                return response?.data?.message ?? this.$t(fallbackKey);
            }

            if (!response) {
                return this.$t(errorMessageKey(error));
            }

            return response?.data?.message ?? this.$t(fallbackKey);
        },
        /**
         * La API entrega '2026-08-03 19:00:00'. Se reordena a mano en vez de
         * construir un Date porque el navegador lo interpretaria como UTC y la
         * fecha mostrada bailaria dos horas respecto a la del servidor.
         */
        formatDateTime(value) {
            if (!value) {
                return '';
            }

            const [datePart, timePart = ''] = value.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hour = '00', minutes = '00'] = timePart.split(':');

            return `${day}-${month}-${year} ${hour}:${minutes}`;
        },
        formatBytes(bytes) {
            if (bytes === null || bytes === undefined || bytes === '') {
                return '';
            }

            const units = ['B', 'KB', 'MB', 'GB', 'TB'];
            let value = Number(bytes);
            let unit = 0;

            while (value >= 1024 && unit < units.length - 1) {
                value = value / 1024;
                unit++;
            }

            return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
        },
        typeLabel(type) {
            const key = 'admin.backups.types.' + type;
            const label = this.$t(key);

            // Un tipo nuevo en el backend no debe pintar la clave cruda en pantalla.
            return label === key ? type : label;
        },
        typeHint(type) {
            const key = 'admin.backups.typeHints.' + type;
            const hint = this.$t(key);

            return hint === key ? '' : hint;
        },
        typeColor(type) {
            // Colores explicitos de Material, nunca 'primary': en este tema
            // primary es #ffffff (themeConfig.js), asi que un chip con `dark`
            // quedaba con texto blanco sobre fondo blanco, es decir, en blanco.
            const colors = {
                manual: 'blue darken-2',
                pre_restore: 'orange darken-2',
                pre_reset: 'orange darken-2',
                annual_close: 'purple',
                uploaded: 'teal'
            };

            return colors[type] ?? 'grey';
        },
        operationLabel(operation) {
            const key = 'admin.backups.operations.' + operation;
            const label = this.$t(key);

            return label === key ? operation : label;
        },
        statusLabel(status) {
            const key = 'admin.backups.statuses.' + status;
            const label = this.$t(key);

            return label === key ? status : label;
        },
        statusColor(status) {
            const colors = {
                running: 'info',
                completed: 'success',
                failed: 'error'
            };

            return colors[status] ?? 'grey';
        }
    }
}
</script>
